import {
  Avatar,
  Box,
  Stack,
  Typography,
  CardMedia,
  Link as MuiLink,
} from '@mui/material';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import { useTheme } from '@mui/material/styles';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ReactMarkdown from 'react-markdown';
import VizualizadorImagem from '../../components/VizualizadorImagem';
import AudioPlayerWithSpeed from '../../components/AudioPlayerWithSpeed';
import PdfPreview from '../../components/PdfPreview';

function formatMessageTime(value) {
  if (!value) return '';
  const raw = String(value).trim();
  const hhmmss = raw.match(/^(\d{2}):(\d{2})(:\d{2})?$/);
  if (hhmmss) return `${hhmmss[1]}:${hhmmss[2]}`;
  const dmyHms = raw.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2})(:\d{2})?$/);
  if (dmyHms) return `${dmyHms[4]}:${dmyHms[5]}`;
  const iso = new Date(raw);
  if (!Number.isNaN(iso.getTime())) {
    const hh = String(iso.getHours()).padStart(2, '0');
    const mm = String(iso.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }
  return raw.slice(0, 5);
}

function getReplyPreview(replyTo) {
  if (!replyTo) return '';
  if (replyTo.msgType === 'file') {
    const rawType =
      (replyTo.msgFileType || `${replyTo.urlFile || ''}`.split('.').pop() || '').toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(rawType)) return 'Foto';
    if (['m4a', 'mp3', 'wav', 'aac', 'ogg', 'webm'].includes(rawType)) return 'Audio';
    if (['mp4', 'mov', '3gpp', 'mkv'].includes(rawType)) return 'Video';
    if (rawType === 'pdf') return 'Documento PDF';
    return 'Arquivo';
  }
  return replyTo.msgText || 'Mensagem';
}

function renderReplyPreview(replyTo, isDark = false) {
  if (!replyTo) return null;
  const textColor = isDark ? '#fff' : 'text.primary';
  if (replyTo.msgType !== 'file') {
    return <Typography variant="body2" sx={{ color: textColor }}>{replyTo.msgText || 'Mensagem'}</Typography>;
  }

  const fileType =
    (replyTo.msgFileType || `${replyTo.urlFile || ''}`.split('.').pop() || '').toLowerCase();
  const url = replyTo.urlFile || '';
  const label = getReplyPreview(replyTo);
  const commonThumbSx = {
    width: 44,
    height: 44,
    borderRadius: 1,
    objectFit: 'cover',
    border: isDark ? '1px solid rgba(255,255,255,0.35)' : '1px solid rgba(0,0,0,0.12)',
    bgcolor: 'background.paper',
  };

  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(fileType) && url) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="img" src={url} alt="midia" sx={commonThumbSx} />
        <Typography variant="body2" sx={{ color: textColor }}>{label}</Typography>
      </Box>
    );
  }
  if (['mp4', 'mov', '3gpp', 'mkv'].includes(fileType) && url) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="video" src={url} muted sx={commonThumbSx} />
        <Typography variant="body2" sx={{ color: textColor }}>{label}</Typography>
      </Box>
    );
  }
  if (['m4a', 'mp3', 'wav', 'aac', 'ogg', 'webm'].includes(fileType)) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GraphicEqIcon fontSize="small" />
        <Typography variant="body2" sx={{ color: textColor }}>{label}</Typography>
      </Box>
    );
  }
  if (fileType === 'pdf') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PictureAsPdfIcon fontSize="small" />
        <Typography variant="body2" sx={{ color: textColor }}>{label}</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <InsertDriveFileIcon fontSize="small" />
      <Typography variant="body2" sx={{ color: textColor }}>{label}</Typography>
    </Box>
  );
}

function isAutomaticMessage(text = '') {
  const upper = String(text).toUpperCase();
  return upper.includes('MENSAGEM AUTOMATICA') || upper.includes('MENSAGEM AUTOMÁTICA');
}

function CustomMarkdownLink({ href }) {
  const theme = useTheme();

  const getDomain = (url) => {
    try {
      const { hostname } = new URL(url);
      return hostname;
    } catch {
      return url;
    }
  };

  return (
    <MuiLink
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        color: theme.palette.secondary.light,
        textDecoration: 'underline',
        fontWeight: 500,
      }}
    >
      {getDomain(href)}
      <OpenInNewIcon fontSize="inherit" />
    </MuiLink>
  );
}

function MensagemCliente({ mensage = {}, onReply }) {
  const { mensage: textoMensagem = '', data, serviceResp, filePath, replyTo } = mensage;
  const theme = useTheme();
  const hideReplyButton = isAutomaticMessage(textoMensagem);
  const fileType = `${filePath || ''}`.split('.').pop()?.toLowerCase();
  const isAudioMessage =
    serviceResp === 'receiveFile' && ['webm', 'ogg', 'mp3', 'wav', 'm4a', 'aac'].includes(fileType);

  function filterTypeArchive(path) {
    const type = `${path.slice(path.lastIndexOf('.') + 1)}`;
    return (
      <Box>
        {['png', 'jpg', 'jpeg'].includes(type) && <VizualizadorImagem arquivo={path} />}
        {type === 'pdf' && (
          <PdfPreview urlFile={path} />
        )}
        {['mp4', '3gpp', 'mov'].includes(type) && (
          <CardMedia component="video" height={210} image={path} controls />
        )}
        {['webm', 'ogg', 'mp3', 'wav', 'm4a', 'aac'].includes(type) && (
          <Box
            sx={{
              minWidth: 0,
              width: { xs: 'min(320px, calc(100vw - 122px))', sm: '100%' },
              maxWidth: { xs: 'calc(100vw - 122px)', sm: 520 },
              overflow: 'hidden',
              p: 1.2,
              borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.22)',
              backgroundColor: theme.palette.primary.main,
              boxShadow: 'none',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <GraphicEqIcon fontSize="small" />
              <Typography variant="caption" fontWeight={700} sx={{ color: '#fff' }}>
                Mensagem de voz
              </Typography>
            </Box>
            <AudioPlayerWithSpeed src={path} tone="outgoing" />
            {!hideReplyButton && (
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.85)',
                  cursor: 'pointer',
                  mt: 0.4,
                  display: 'block',
                }}
                onClick={() => onReply?.(mensage)}
              >
                Responder
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }

  function getDynamicPadding(text) {
    const length = text?.length || 0;
    if (length > 300) return 3.5;
    if (length > 150) return 3;
    if (length > 100) return 1;
    return 1;
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 1,
        }}
      >
        <Avatar
          alt="Nome Avatar"
          sx={{
            marginRight: 2,
            width: 35,
            height: 35,
            background: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }}
        />
        <Stack
          sx={{
            width: 'fit-content',
            maxWidth: { xs: 'calc(100% - 52px)', sm: 'calc(100% - 64px)' },
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              marginRight: { xs: 0, sm: 10, md: 20 },
              width: 'fit-content',
              backgroundColor: isAudioMessage ? 'transparent' : theme.palette.primary.main,
              padding: isAudioMessage
                ? 0
                : {
                    xs: 1.5,
                    sm: getDynamicPadding(textoMensagem),
                  },
              borderRadius: isAudioMessage ? 0 : 1,
              color: '#fff',
              maxWidth: { xs: '90%', sm: '80%', md: '600px' },
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
              alignSelf: 'flex-start',
            }}
          >
            {replyTo && (
              <Box
                sx={{
                  mb: 1,
                  p: 0.8,
                  borderRadius: 1,
                  borderLeft: '3px solid rgba(255,255,255,0.7)',
                  bgcolor: 'rgba(0,0,0,0.18)',
                }}
              >
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  Respondendo
                </Typography>
                {renderReplyPreview(replyTo, true)}
              </Box>
            )}

            {serviceResp === 'mensage' && (
              <ReactMarkdown components={{ a: CustomMarkdownLink }}>
                {textoMensagem}
              </ReactMarkdown>
            )}

            {serviceResp === 'receiveFile' && filterTypeArchive(filePath)}

            {!hideReplyButton && !isAudioMessage && (
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  cursor: 'pointer',
                  mt: 0.6,
                  display: 'block',
                }}
                onClick={() => onReply?.(mensage)}
              >
                Responder
              </Typography>
            )}
          </Box>
          <Typography
            variant="caption"
            sx={{ textAlign: 'start', color: theme.palette.text.disabled, mt: 0.35 }}
          >
            {formatMessageTime(data)}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

export default MensagemCliente;
